import React, { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

// Animated starfield + nebula backdrop. Port of the final reference HTML.
// Dark mode: deep near-black sky, 4 radial nebula clouds, 250 parallax
// stars with twinkle + trails + glow for the brightest ones.
// Light mode: clean white, same star parallax but dimmer and warmer
// (no nebulae, no trails, no glow).
export default function MeshBackground() {
  const { theme } = useTheme();
  const canvasRef = useRef(null);
  // Keep theme on a ref so the rAF loop reads the latest value without
  // re-running the effect (which would reset the star positions).
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    let raf = 0;
    let starT = 0;

    // ── Star field ──
    // x/y in [-1, 1] (relative to centre). z in (0, 1] is depth — when
    // it crosses 0 the star is "reset" to far away again, giving the
    // parallax "zooming through space" effect.
    const STAR_COUNT = 250;
    const stars = Array.from({ length: STAR_COUNT }, () => ({
      x: (Math.random() - 0.5) * 2,
      y: (Math.random() - 0.5) * 2,
      z: Math.random(),
      speed: Math.random() * 0.0018 + 0.0006,
      hue: 190 + Math.random() * 40,             // cyan → indigo range
      twinkle: Math.random() * Math.PI * 2,
    }));

    // ── Nebula clouds (dark only) ──
    const nebulae = [
      { x: 0.25, y: 0.35, hue: 210, r: 0.45 },
      { x: 0.75, y: 0.60, hue: 270, r: 0.38 },
      { x: 0.55, y: 0.20, hue: 185, r: 0.32 },
      { x: 0.15, y: 0.75, hue: 230, r: 0.30 },
    ];

    const resize = () => {
      canvas.width = Math.max(document.documentElement.clientWidth, window.innerWidth || 320);
      canvas.height = Math.max(document.documentElement.clientHeight, window.innerHeight || 600);
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      const W = canvas.width, H = canvas.height;
      const cx = W * 0.5, cy = H * 0.5;
      const dark = themeRef.current === 'dark';
      starT += 0.016;

      // Base fill — near-black in dark, pure white in light.
      ctx.fillStyle = dark ? '#020508' : '#ffffff';
      ctx.fillRect(0, 0, W, H);

      // Nebulae (dark only) — large soft radial gradients in cool hues.
      if (dark) {
        for (const n of nebulae) {
          const g = ctx.createRadialGradient(n.x * W, n.y * H, 0, n.x * W, n.y * H, n.r * W);
          g.addColorStop(0,   `hsla(${n.hue},70%,25%,0.18)`);
          g.addColorStop(0.5, `hsla(${n.hue + 20},60%,18%,0.08)`);
          g.addColorStop(1,   'transparent');
          ctx.fillStyle = g;
          ctx.fillRect(0, 0, W, H);
        }
      }

      // Stars — parallax + twinkle + (dark only) motion trail + glow.
      for (const s of stars) {
        s.z -= s.speed;
        if (s.z <= 0.001) {
          s.x = (Math.random() - 0.5) * 2;
          s.y = (Math.random() - 0.5) * 2;
          s.z = 1.0;
        }

        const sx = cx + (s.x / s.z) * cx * 0.85;
        const sy = cy + (s.y / s.z) * cy * 0.85;
        if (sx < 0 || sx > W || sy < 0 || sy > H) continue;

        const depth = 1 - s.z;
        const radius = Math.max(0.3, depth * 2.8);
        const twinkle = 0.75 + 0.25 * Math.sin(starT * 2.5 + s.twinkle);
        const alpha = dark
          ? Math.min(1, depth * 1.4) * twinkle
          : Math.min(0.6, depth * 0.8) * twinkle;

        // Trail for fast/near stars in dark mode only.
        if (dark && depth > 0.6) {
          const prevZ = s.z + s.speed * 4;
          const px = cx + (s.x / prevZ) * cx * 0.85;
          const py = cy + (s.y / prevZ) * cy * 0.85;
          const tg = ctx.createLinearGradient(px, py, sx, sy);
          tg.addColorStop(0, 'transparent');
          tg.addColorStop(1, `hsla(${s.hue},80%,90%,${alpha * 0.5})`);
          ctx.beginPath();
          ctx.moveTo(px, py); ctx.lineTo(sx, sy);
          ctx.strokeStyle = tg;
          ctx.lineWidth = radius * 0.6;
          ctx.stroke();
        }

        ctx.beginPath();
        ctx.arc(sx, sy, radius, 0, Math.PI * 2);
        ctx.fillStyle = dark
          ? `hsla(${s.hue},70%,92%,${alpha})`
          : `hsla(${s.hue},50%,40%,${alpha})`;
        ctx.fill();

        // Glow halo for the brightest near stars in dark mode.
        if (dark && depth > 0.75) {
          const glow = ctx.createRadialGradient(sx, sy, 0, sx, sy, radius * 5);
          glow.addColorStop(0, `hsla(${s.hue},80%,80%,${alpha * 0.3})`);
          glow.addColorStop(1, 'transparent');
          ctx.fillStyle = glow;
          ctx.fillRect(sx - radius * 5, sy - radius * 5, radius * 10, radius * 10);
        }
      }

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
    // Intentionally no deps — the loop reads theme from themeRef so it
    // never re-mounts (which would reset the star positions and make
    // the field "jump" every time the user toggles dark/light).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <canvas id="bg-canvas" ref={canvasRef} aria-hidden="true" />;
}
